import { useEffect, useRef } from 'react';
import { Client, type IMessage } from '@stomp/stompjs';
import { useAuthStore } from '@/shared/auth/store';
import type { ChatMessage } from '@/shared/api/chat';

interface UseChatSocketArgs {
  meetingId: string;
  enabled: boolean;
  /** 새 메시지 수신 시 호출 (id 기준 디덥은 호출 측 책임) */
  onMessage: (msg: ChatMessage) => void;
  /** (재)연결 직후 호출 — 끊긴 동안 놓친 메시지 재동기화용 */
  onResync?: () => void;
}

function brokerUrl(): string {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}/api/ws`;
}

/**
 * 모임 채팅 실시간 구독 (STOMP over WebSocket).
 * 전송은 기존 REST POST 가 담당하고, 이 훅은 수신 전용.
 * 연결이 끊기면 stompjs 가 자동 재연결하고, 재연결 시 onResync 로 공백을 메운다.
 */
export function useChatSocket({ meetingId, enabled, onMessage, onResync }: UseChatSocketArgs) {
  // 콜백을 ref 로 잡아 클라이언트 재생성 없이 최신 핸들러 사용
  const onMessageRef = useRef(onMessage);
  const onResyncRef = useRef(onResync);
  onMessageRef.current = onMessage;
  onResyncRef.current = onResync;

  useEffect(() => {
    if (!enabled || !meetingId) return;

    const client = new Client({
      brokerURL: brokerUrl(),
      reconnectDelay: 4000,
      heartbeatIncoming: 10000,
      heartbeatOutgoing: 10000,
      // 매 연결 시 최신 토큰 주입 (refresh 로 갱신될 수 있음)
      beforeConnect: () => {
        const token = useAuthStore.getState().accessToken;
        client.connectHeaders = token ? { Authorization: `Bearer ${token}` } : {};
      },
      onConnect: () => {
        client.subscribe(`/topic/meetings/${meetingId}/chat`, (frame: IMessage) => {
          try {
            const msg = JSON.parse(frame.body) as ChatMessage;
            onMessageRef.current(msg);
          } catch {
            // 파싱 불가 프레임 무시
          }
        });
        onResyncRef.current?.();
      },
    });

    client.activate();
    return () => {
      void client.deactivate();
    };
  }, [meetingId, enabled]);
}
